/** @jsx jsx */
import React, { useCallback, type ReactElement } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { DrawerCloseButton } from '@atlaskit/drawer/drawer-close-button';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import type { Stage, Stages } from '@atlaskit/progress-tracker/types';
import { ProgressTracker } from '@atlaskit/progress-tracker';

import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';
import type { ButtonGroupProps } from '@atlaskit/button/button-group';
import { useIntl, defineMessage, type MessageDescriptor } from '@atlassian/jira-intl';
import type { CreateFlowVariant } from './StageProps.types.tsx';

const styles = cssMap({
	container: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		height: '100%',
	},
	mainContentInner: {
		maxWidth: '800px',
		margin: '0 auto',
	},
	mainContent: {
		paddingTop: token('space.400'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.200'),
		flex: '1 0 auto',
	},
	footerContent: {
		flexShrink: 0,
	},
	footerContentInner: {
		maxWidth: '800px',
		margin: '0 auto',
	},
	dividerStyle: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		marginTop: token('space.200'),
	},
});

type StepSequence = string[];
type StepNameMap = { [key: string]: MessageDescriptor };

const UNKNOWN_STEP_ORDER: StepSequence = ['create', 'unknown'];
const OAUTH1_STEP_ORDER: StepSequence = ['create', 'review', 'reciprocal-prompt', 'confirmation'];
const OAUTH2_STEP_ORDER: StepSequence = ['create', 'review', 'connect'];

const createStepNameMessage = defineMessage({
	defaultMessage: 'Create link',
	id: 'applinks.administration.ui.creation-flow-dialog.create-step-name',
	description: 'Name of the step for creating a new application link.',
});

const reviewStepNameMessage = defineMessage({
	defaultMessage: 'Review link',
	id: 'applinks.administration.ui.creation-flow-dialog.review-step-name',
	description: 'Name of the step for reviewing the new application link.',
});

const reciprocalPromptStepNameNonfinalMessage = defineMessage({
	defaultMessage: 'Create reciprocal link',
	id: 'applinks.administration.ui.creation-flow-dialog.reciprocal-prompt-name',
	description: 'Name of the step for creating a reciprocal application link.',
});

const connectStepNameMessage = defineMessage({
	defaultMessage: 'Configure link',
	id: 'applinks.administration.ui.creation-flow-dialog.connect-step-name',
	description: "Name of the step for securing the application link's connection",
});

const confirmationStepNameNonfinalMessage = defineMessage({
	defaultMessage: 'Confirmation',
	id: 'applinks.administration.ui.creation-flow-dialog.confirmation-step-name',
	description: 'Name of the step for confirming the new application link.',
});

const unknownStepNameNonfinalMessage = defineMessage({
	defaultMessage: 'Configure link',
	id: 'applinks.administration.ui.creation-flow-dialog.unknown-step-name',
	description: 'Name of the step for configuring the application link.',
});

const stepNames: StepNameMap = {
	create: createStepNameMessage,
	review: reviewStepNameMessage,
	'reciprocal-prompt': reciprocalPromptStepNameNonfinalMessage,
	connect: connectStepNameMessage,
	confirmation: confirmationStepNameNonfinalMessage,
	unknown: unknownStepNameNonfinalMessage,
};

const determineStepPercentage = (
	currentStep: string,
	targetStep: string,
	sequence: StepSequence,
): number => {
	if (currentStep === 'manual-create' && targetStep === 'create') {
		return 50; // Special case for manual create step
	}

	const currentIndex = sequence.indexOf(currentStep);
	const targetIndex = sequence.indexOf(targetStep);

	if (currentIndex > targetIndex) {
		return 100;
	}
	if (currentIndex === targetIndex) {
		return 0;
	}
	return 0;
};

const determineStepStatus = (
	currentStep: string,
	targetStep: string,
	sequence: StepSequence,
): Stage['status'] => {
	if (currentStep === 'manual-create' && targetStep === 'create') {
		return 'current'; // Special case for manual create step
	}

	const currentIndex = sequence.indexOf(currentStep);
	const targetIndex = sequence.indexOf(targetStep);

	if (currentIndex > targetIndex) {
		return 'disabled';
	}
	if (currentIndex === targetIndex) {
		return 'current';
	}
	return 'unvisited';
};

const buildSteps = (
	step: string,
	sequence: StepSequence,
	formatMessage: (desc: MessageDescriptor) => string,
): Stages => {
	return sequence.map((s) => ({
		id: s,
		label: formatMessage(stepNames[s]),
		percentageComplete: determineStepPercentage(step, s, sequence),
		status: determineStepStatus(step, s, sequence),
		href: '#',
	}));
};

const buildProgressTrackerItems = (
	step: string,
	variant: CreateFlowVariant | null,
	formatMessage: (desc: MessageDescriptor) => string,
): Stages => {
	if (variant === null) {
		return buildSteps(step, UNKNOWN_STEP_ORDER, formatMessage);
	}

	switch (variant) {
		case 'OAUTH1.0':
			return buildSteps(step, OAUTH1_STEP_ORDER, formatMessage);
		case 'TUNNEL':
			return buildSteps(step, OAUTH1_STEP_ORDER, formatMessage); // Tunnel uses the same steps as OAUTH1.0
		case 'OAUTH2.0':
			return buildSteps(step, OAUTH2_STEP_ORDER, formatMessage);
		default:
			return [];
	}
};

export type HeaderProps = {
	kind: string;
	variant: CreateFlowVariant | null;
};

export const Header = (props: HeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const getItems = useCallback(() => {
		return buildProgressTrackerItems(props.kind, props.variant, formatMessage);
	}, [formatMessage, props.kind, props.variant]);

	return (
		<Inline>
			<DrawerCloseButton icon={CrossIcon} />
			<ProgressTracker items={getItems()} spacing="comfortable" />
		</Inline>
	);
};

export type FooterProps = {
	buttons: ReactElement<ButtonGroupProps>;
};

export const Footer = (props: FooterProps): React.JSX.Element => {
	return (
		<Stack xcss={styles.footerContentInner} grow="fill">
			<Inline alignInline="end" space="space.050">
				{props.buttons}
			</Inline>
		</Stack>
	);
};

export type SubpageTemplateProps = {
	variant: CreateFlowVariant | null;
	kind: string;
	buttons: ReactElement<ButtonGroupProps>;
	content: ReactElement;
};

export const SubpageTemplate = (props: SubpageTemplateProps): React.JSX.Element => (
	<Flex xcss={styles.container} direction="column">
		{/* Header */}
		<Header variant={props.variant} kind={props.kind} />
		<hr role="presentation" css={styles.dividerStyle} />

		{/* Content */}
		<Box xcss={styles.mainContent}>
			<Box xcss={styles.mainContentInner}>{props.content}</Box>
		</Box>

		{/* Footer */}
		<Stack xcss={styles.footerContent} space="space.100">
			<hr role="presentation" css={styles.dividerStyle} />
			<Footer buttons={props.buttons} />
		</Stack>
	</Flex>
);
